// Ported verbatim from mobile src/types/analysisWs.ts.
import type { BrainerCoreInner } from './brainerCore';

/** Composite key used to identify a subscription, e.g. "BTCUSDT|15m|futures|binance" */
export type AnalysisSubKey = string;

/** Markets supported by the analysis subscription. */
export type AnalysisMarket = 'futures' | 'spot';

/** Exchanges supported by the analysis subscription. */
export type AnalysisExchange = 'binance' | 'mexc';

/**
 * Lifecycle states for a single (symbol, timeframe) subscription.
 *  idle    — registered but subscribe message not yet sent (socket not open)
 *  loading — subscribe sent, waiting for the first push (≤10 s window)
 *  timeout — 10 s elapsed with no data; user can retry
 *  ready   — has data; subsequent candle-close pushes update in place silently
 */
export type AnalysisSubStatus = 'idle' | 'loading' | 'timeout' | 'ready';

export interface AnalysisSubState {
  status: AnalysisSubStatus;
  data: BrainerCoreInner | null;
  /** Server-sent ms-epoch timestamp of the last push; null before first push. */
  timestamp: number | null;
}

/** Shape of inbound messages from the server. */
export interface AnalysisWsInbound {
  symbol: string;
  timeframe: string;
  timestamp: number;
  /** Present on server pushes; lets us disambiguate spot vs futures panels. */
  market?: AnalysisMarket;
  bar_closed?: boolean;
  data: BrainerCoreInner;
}

/** Shape of outbound messages sent by the app. */
export interface AnalysisWsOutbound {
  action: 'subscribe' | 'unsubscribe';
  symbol: string;
  timeframe: string;
  market?: AnalysisMarket;
  exchange?: AnalysisExchange;
  network?: 'mainnet' | 'testnet';
}

/** Build the canonical key for a (symbol, timeframe, market, exchange) tuple. */
export function makeSubKey(
  symbol: string,
  timeframe: string,
  market: AnalysisMarket,
  exchange: AnalysisExchange,
): AnalysisSubKey {
  return `${symbol}|${timeframe}|${market}|${exchange}`;
}

/** Parse a key back into its components — used when re-subscribing on reconnect. */
export function parseSubKey(key: AnalysisSubKey): {
  symbol: string;
  timeframe: string;
  market: AnalysisMarket;
  exchange: AnalysisExchange;
} {
  const parts = key.split('|');
  return {
    symbol: parts[0],
    timeframe: parts[1],
    market: (parts[2] as AnalysisMarket) ?? 'futures',
    exchange: (parts[3] as AnalysisExchange) ?? 'binance',
  };
}

/**
 * Find the active subscription key matching an inbound (symbol, timeframe).
 * Inbound pushes from the server carry no market — but the client only ever
 * has one active market per (symbol, timeframe), so we can route by prefix.
 */
export function findActiveSubKey(
  activeKeys: AnalysisSubKey[],
  symbol: string,
  timeframe: string,
): AnalysisSubKey | undefined {
  const prefix = `${symbol}|${timeframe}|`;
  return activeKeys.find((k) => k.startsWith(prefix));
}

/**
 * Resolve an inbound push to a subscription key, disambiguating panels that
 * share a (symbol, timeframe) but differ by market and/or exchange — the case
 * the comparison grid introduces (mobile only ever had one active sub).
 *
 * Precision order: exact (symbol|tf|market|exchange) → unique exchange match →
 * market within exchange → first prefix match (legacy single-sub fallback).
 */
export function resolveSubKey(
  activeKeys: AnalysisSubKey[],
  symbol: string,
  timeframe: string,
  market?: string,
  exchange?: string,
): AnalysisSubKey | undefined {
  if (market && exchange) {
    const exact = `${symbol}|${timeframe}|${market}|${exchange}`;
    if (activeKeys.includes(exact)) return exact;
  }

  const prefix = `${symbol}|${timeframe}|`;
  const candidates = activeKeys.filter((k) => k.startsWith(prefix));
  if (candidates.length <= 1) return candidates[0];

  if (exchange) {
    const byExchange = candidates.filter((k) => k.endsWith(`|${exchange}`));
    if (byExchange.length === 1) return byExchange[0];
    if (byExchange.length > 1 && market) {
      const m = byExchange.find((k) => k.split('|')[2] === market);
      if (m) return m;
    }
    if (byExchange.length > 0) return byExchange[0];
  }

  if (market) {
    const m = candidates.find((k) => k.split('|')[2] === market);
    if (m) return m;
  }

  return candidates[0];
}
